import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Brand } from '../../lib/types';
import { useUpdateBrand } from '../../lib/api/brands';
import { useToast } from '../ui/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  brand: Brand;
}

interface ContactDraft {
  name: string;
  role: string;
  email: string;
  phone: string;
}

const emptyContact: ContactDraft = { name: '', role: '', email: '', phone: '' };

const ContactBlock = ({
  label,
  contact,
  onChange,
  onRemove,
}: {
  label: string;
  contact: ContactDraft;
  onChange: (c: ContactDraft) => void;
  onRemove?: () => void;
}) => (
  <div
    style={{
      padding: '14px 14px 4px',
      background: 'var(--bg-subtle)',
      borderRadius: 'var(--r-md)',
      border: '1px solid var(--border-soft)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--ink-4)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      {onRemove && (
        <button
          type="button"
          className="link"
          style={{ fontSize: 11.5 }}
          onClick={onRemove}
        >
          Remove
        </button>
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <Field label="Name">
        <input
          className="input"
          value={contact.name}
          onChange={(e) => onChange({ ...contact, name: e.target.value })}
          placeholder="e.g. Giulia Bianchi"
        />
      </Field>
      <Field label="Role">
        <input
          className="input"
          value={contact.role}
          onChange={(e) => onChange({ ...contact, role: e.target.value })}
          placeholder="e.g. Marketing Lead"
        />
      </Field>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <Field label="Email">
        <input
          className="input"
          type="email"
          value={contact.email}
          onChange={(e) => onChange({ ...contact, email: e.target.value })}
          placeholder="contact@brand.com"
        />
      </Field>
      <Field label="Phone">
        <input
          className="input"
          value={contact.phone}
          onChange={(e) => onChange({ ...contact, phone: e.target.value })}
          placeholder="+39 000 00 00"
        />
      </Field>
    </div>
  </div>
);

export const BrandContactModal = ({ open, onClose, brand }: Props) => {
  const toast = useToast();
  const updateBrand = useUpdateBrand();
  const [primary, setPrimary] = useState<ContactDraft>(emptyContact);
  const [secondary, setSecondary] = useState<ContactDraft>(emptyContact);
  const [tertiary, setTertiary] = useState<ContactDraft>(emptyContact);
  const [showSecondary, setShowSecondary] = useState(false);
  const [showTertiary, setShowTertiary] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrimary({
      name: brand.contact_name || '',
      role: brand.contact_role || '',
      email: brand.contact_email || '',
      phone: brand.contact_phone || '',
    });
    setSecondary({
      name: brand.contact_name_2 || '',
      role: brand.contact_role_2 || '',
      email: brand.contact_email_2 || '',
      phone: brand.contact_phone_2 || '',
    });
    setTertiary({
      name: brand.contact_name_3 || '',
      role: brand.contact_role_3 || '',
      email: brand.contact_email_3 || '',
      phone: brand.contact_phone_3 || '',
    });
    const has2 =
      !!(brand.contact_name_2 || brand.contact_email_2 || brand.contact_phone_2 || brand.contact_role_2);
    const has3 =
      !!(brand.contact_name_3 || brand.contact_email_3 || brand.contact_phone_3 || brand.contact_role_3);
    setShowSecondary(has2 || has3);
    setShowTertiary(has3);
  }, [open, brand]);

  const submit = async () => {
    try {
      await updateBrand.mutateAsync({
        id: brand.id,
        patch: {
          contact_name: primary.name,
          contact_role: primary.role,
          contact_email: primary.email,
          contact_phone: primary.phone,
          contact_name_2: showSecondary ? secondary.name : '',
          contact_role_2: showSecondary ? secondary.role : '',
          contact_email_2: showSecondary ? secondary.email : '',
          contact_phone_2: showSecondary ? secondary.phone : '',
          contact_name_3: showTertiary ? tertiary.name : '',
          contact_role_3: showTertiary ? tertiary.role : '',
          contact_email_3: showTertiary ? tertiary.email : '',
          contact_phone_3: showTertiary ? tertiary.phone : '',
        },
      });
      toast.push('Brand contacts saved');
      onClose();
    } catch (err) {
      console.error(err);
      toast.push('Could not save brand contacts');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${brand.name} — contacts`}
      maxWidth={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={updateBrand.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ContactBlock
          label="Contact 1"
          contact={primary}
          onChange={setPrimary}
        />
        {showSecondary && (
          <ContactBlock
            label="Contact 2"
            contact={secondary}
            onChange={setSecondary}
            onRemove={() => {
              setSecondary(emptyContact);
              setShowSecondary(false);
              // Also collapse #3 since it's tied to #2 existing
              setTertiary(emptyContact);
              setShowTertiary(false);
            }}
          />
        )}
        {showSecondary && showTertiary && (
          <ContactBlock
            label="Contact 3"
            contact={tertiary}
            onChange={setTertiary}
            onRemove={() => {
              setTertiary(emptyContact);
              setShowTertiary(false);
            }}
          />
        )}
        {!showSecondary && (
          <button
            type="button"
            className="link"
            style={{ fontSize: 12, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowSecondary(true)}
          >
            <Icon name="plus" size={12} /> Add a second contact
          </button>
        )}
        {showSecondary && !showTertiary && (
          <button
            type="button"
            className="link"
            style={{ fontSize: 12, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowTertiary(true)}
          >
            <Icon name="plus" size={12} /> Add a third contact
          </button>
        )}
      </div>
    </Modal>
  );
};

export default BrandContactModal;
